import { useState } from "react";
import {
  useCreate,
  useUpdate,
  useOne,
  useGo,
  useList,
  useCustom,
} from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Loader2, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormField,
  FormCard,
  FormHeader,
  FormStickyActions,
  FormSpacer,
} from "@/components/forms";
import type { AdminUser, AdminUserCreatePayload } from "./types";
import type { Role } from "@/resources/roles/types";

interface AdminUserFormProps {
  action: "create" | "edit";
}

/**
 * AdminUserForm
 *
 * Форма управления admin-users
 *
 * @returns {React.ReactElement} React компонент
 */
export function AdminUserForm({ action }: AdminUserFormProps) {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  // Fetch data for edit
  const oneResult = useOne<AdminUser>({
    resource: "admin-users",
    id: id ? Number(id) : undefined,
    queryOptions: {
      enabled: action === "edit" && !!id,
    },
  });

  const isFetching = oneResult.query.isLoading;

  // Mutations
  const createMutation = useCreate();
  const updateMutation = useUpdate();

  const isPending =
    createMutation.mutation.isPending ||
    updateMutation.mutation.isPending ||
    isFetching;

  if (action === "edit" && isFetching) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const initialFormData: AdminUserCreatePayload = oneResult.result
    ? {
        email: oneResult.result.email,
        roleId: oneResult.result.roleId ?? 0,
        password: "",
      }
    : {
        email: "",
        password: "",
        roleId: 0,
      };

  const formKey =
    action === "edit" ? `edit-${oneResult.result?.id ?? "loading"}` : "create";

  return (
    <AdminUserFormFields
      key={formKey}
      action={action}
      id={id ? Number(id) : undefined}
      initialFormData={initialFormData}
      isPending={isPending}
      onCreate={(values) =>
        createMutation.mutate(
          {
            resource: "admin-users",
            values,
          },
          {
            onSuccess: () => go({ to: "/admin-users" }),
          }
        )
      }
      onUpdate={(values) =>
        updateMutation.mutate(
          {
            resource: "admin-users",
            id: Number(id),
            values,
          },
          {
            onSuccess: () => go({ to: "/admin-users" }),
          }
        )
      }
      onCancel={() => go({ to: "/admin-users" })}
    />
  );
}

/**
 * AdminUsersCreate
 *
 * Форма управления admin-users
 *
 * @returns {React.ReactElement} React компонент
 */
export const AdminUsersCreate = () => <AdminUserForm action="create" />;
/**
 * AdminUsersEdit
 *
 * Форма управления admin-users
 *
 * @returns {React.ReactElement} React компонент
 */
export const AdminUsersEdit = () => <AdminUserForm action="edit" />;

interface AdminUserFormFieldsProps {
  action: "create" | "edit";
  id?: number;
  initialFormData: AdminUserCreatePayload;
  isPending: boolean;
  onCreate: (values: AdminUserCreatePayload) => void;
  onUpdate: (values: AdminUserCreatePayload) => void;
  onCancel: () => void;
}

function AdminUserFormFields({
  action,
  initialFormData,
  isPending,
  onCreate,
  onUpdate,
  onCancel,
}: AdminUserFormFieldsProps) {
  const [formData, setFormData] =
    useState<AdminUserCreatePayload>(initialFormData);
  const rolesResult = useList<Role>({
    resource: "roles",
    pagination: { currentPage: 1, pageSize: 200 },
    sorters: [{ field: "displayName", order: "asc" }],
  });
  const roles = rolesResult.result?.data ?? [];
  const isRolesLoading = rolesResult.query.isLoading;

  // Проверяем, настроен ли SMTP на бэкенде
  const smtpStatusResult = useCustom<{ configured: boolean }>({
    url: "/admin-users/smtp-status",
    method: "get",
  });
  const smtpConfigured = smtpStatusResult.result?.data.configured ?? false;
  const isSmtpStatusLoading = smtpStatusResult.query.isLoading;

  // Пароль обязателен только при create И если SMTP не настроен
  const passwordRequired = action === "create" && !smtpConfigured;
  const showPasswordField = action === "edit" || !smtpConfigured;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (action === "create") {
      const createData: AdminUserCreatePayload = {
        email: formData.email,
        roleId: formData.roleId,
      };
      // При SMTP-flow пароль не передаём — бэкенд отправит приглашение
      if (!smtpConfigured && formData.password) {
        createData.password = formData.password;
      }
      onCreate(createData);
      return;
    }

    const updateData = { ...formData };
    if (!updateData.password) {delete updateData.password;}
    onUpdate(updateData);
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={action === "create" ? "Новый администратор" : "Редактирование"}
        description="Учетные данные и доступы администратора"
        onBack={onCancel}
        sticky
      />

      <form onSubmit={handleSubmit} className="relative">
        <div className="max-w-xl pb-24">
          <FormCard
            title="Данные учетной записи"
            description="Основная информация для входа"
          >
            <div className="space-y-4">
              <FormField label="Email" name="email" required>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={isPending}
                />
              </FormField>

              {action === "create" && smtpConfigured && !isSmtpStatusLoading && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    SMTP настроен — на указанный email будет отправлено письмо
                    со ссылкой для установки пароля. Администратор задаст пароль
                    сам.
                  </AlertDescription>
                </Alert>
              )}

              {showPasswordField && (
                <FormField
                  label="Пароль"
                  name="password"
                  required={passwordRequired}
                  hint={
                    action === "edit"
                      ? "Оставьте пустым, если не хотите менять"
                      : undefined
                  }
                >
                  <Input
                    type="password"
                    required={passwordRequired}
                    value={formData.password ?? ""}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    disabled={isPending}
                  />
                </FormField>
              )}

              <FormField label="Роль" name="roleId" required>
                <Select
                  value={formData.roleId ? String(formData.roleId) : ""}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, roleId: Number(value) })
                  }
                  disabled={isPending || isRolesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите роль" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role.id} value={String(role.id)}>
                        {role.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
          </FormCard>
        </div>

        <FormStickyActions
          primaryLabel={action === "create" ? "Создать" : "Сохранить"}
          secondaryLabel="Отмена"
          onPrimary={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
          onSecondary={onCancel}
          isPending={isPending}
          pendingLabel={action === "create" ? "Создание..." : "Сохранение..."}
          secondaryVariant="secondary"
        />
      </form>

      <FormSpacer />
    </div>
  );
}
